import { Entity, FieldType, PersistenceType } from 'gossamer-unified';
import { Event } from './Event';
import { Institution } from './Institution';
import { Ticket } from './Ticket';
import { UserView } from './UserView';
import { BookingPaymentTerms } from './BookingPaymentTerms';
import { EVENTS_I_HOST } from './SecurityRules';

export type PaymentInStatus =
  | 'created'
  | 'cancelled_event'
  | 'cancelled_booking'
  | 'refunded_net'
  | 'refunded_gross'
  | 'paid_out';

export interface PaymentInRow {
  id: number;
  created_at: string;
  cancelled_at: string;
  paid_at: string;
  status: PaymentInStatus;
  user_id: string | null;
  institution_id: number | null;
  booking_payment_terms_id: number | null;
  event_id: number | null;
  ticket_id: number | null;
  item_description: string | null;
  stripe_payment_intent_id: string;
  stripe_charge_id: string | null;
  stripe_balance_transaction_id: string | null;
  currency: string;
  gross_amount: number;
  booking_fee: number;
  net_amount: number;
}

export const PaymentIn: Entity<PaymentInRow> = {
  id: 'PaymentIn',
  title: 'PaymentIn',
  service: 'main',
  rowLabel: (row: PaymentInRow) => row.stripe_payment_intent_id,
  primaryKeyFields: ['id'],
  pluralTitle: 'Payments In',
  allowAddRows: false,
  allowDeleteRows: false,
  source: {
    tableName: 'public.payment_in',
  },
  security: [
    {
      role: 'user',
      action: 'read',
      access: 'conditional',
      condition: `user_id = '{userId}' OR event_id IN ${EVENTS_I_HOST}`,
    },
    {
      role: 'admin',
      action: 'read',
      access: 'conditional',
      condition: `user_id = '{userId}' OR event_id IN ${EVENTS_I_HOST} OR institution_id = {adminInstitutionId}`,
    },
    { role: 'staff', action: 'read', access: 'all' },
  ],
  defaultSort: [['created_at', 'DESC']],
  defaultFilters: [{ fieldId: 'event_id', operator: 'eq', value: '' }],
  fields: {
    id: {
      seq: 5,
      type: FieldType.Integer,
      label: 'Id',
      persistence: PersistenceType.BackEndManaged,
      mandatory: true,
      autoIncrement: true,
    },
    created_at: {
      seq: 10,
      label: 'Created At',
      type: FieldType.Datetime,
      persistence: PersistenceType.BackEndManaged,
      listColumn: true,
    },
    cancelled_at: {
      seq: 15,
      label: 'Cancelled At',
      type: FieldType.Datetime,
      persistence: PersistenceType.BackEndManaged,
    },
    paid_at: {
      seq: 20,
      label: 'Paid At',
      type: FieldType.Datetime,
      persistence: PersistenceType.BackEndManaged,
      mandatory: true,
      listColumn: true,
    },
    status: {
      seq: 25,
      label: 'Status',
      type: FieldType.Options,
      mandatory: true,
      listColumn: true,
      options: [
        { id: 'created', label: 'Created' },
        { id: 'cancelled_event', label: 'Cancelled (event)' },
        { id: 'cancelled_booking', label: 'Cancelled (booking)' },
        { id: 'refunded_net', label: 'Refunded (net)' },
        { id: 'refunded_gross', label: 'Refunded (gross)' },
        { id: 'paid_out', label: 'Paid out' },
      ],
      persistence: PersistenceType.BackEndManaged,
    },
    user_id: {
      seq: 30,
      type: FieldType.Reference,
      label: 'User',
      listColumn: true,
      refEntity: UserView,
    },
    institution_id: {
      seq: 33,
      type: FieldType.Reference,
      label: 'Institution',
      listColumn: true,
      refEntity: Institution,
    },
    booking_payment_terms_id: {
      seq: 34,
      type: FieldType.Reference,
      label: 'Booking Payment Terms',
      refEntity: BookingPaymentTerms,
      listColumn: true,
    },
    event_id: {
      seq: 35,
      type: FieldType.Reference,
      label: 'Event',
      listColumn: true,
      refEntity: Event,
    },
    ticket_id: {
      seq: 40,
      type: FieldType.Reference,
      label: 'Ticket',
      listColumn: true,
      refEntity: Ticket,
    },
    item_description: {
      seq: 45,
      type: FieldType.Text,
      label: 'Item Description',
      maxLength: 255,
    },
    stripe_payment_intent_id: {
      seq: 50,
      label: 'Stripe PaymentIntent',
      type: FieldType.Text,
      mandatory: true,
      listColumn: true,
    },
    stripe_charge_id: {
      seq: 60,
      label: 'Stripe Charge',
      type: FieldType.Text,
      listColumn: true,
    },
    stripe_balance_transaction_id: {
      seq: 70,
      label: 'Stripe Balance Txn',
      type: FieldType.Text,
    },
    currency: {
      seq: 80,
      label: 'Currency',
      type: FieldType.Text,
      mandatory: true,
      listColumn: true,
    },
    gross_amount: {
      seq: 90,
      label: 'Gross Amount',
      type: FieldType.Decimal,
      decimalDigits: 2,
      staticPrefix: '£',
      mandatory: true,
      listColumn: true,
    },
    booking_fee: {
      seq: 100,
      label: 'Booking Fee',
      type: FieldType.Decimal,
      decimalDigits: 2,
      staticPrefix: '£',
      mandatory: true,
      listColumn: true,
    },
    net_amount: {
      seq: 110,
      label: 'Net Amount',
      type: FieldType.Decimal,
      decimalDigits: 2,
      staticPrefix: '£',
      mandatory: true,
      listColumn: true,
    },
  },
};
